// @flow
import * as React from 'react';
import Logging from '@ta/common.logging';

import { aggregateTrackingStack, MentionsTrackingContext } from './MentionsTrackingContext';
import MentionsInteractionContext from './MentionsInteractionContext';

type Trackers = {|
  feedClick: ((SyntheticEvent<*> | MouseEvent) => void) | null,
  showMore: ((SyntheticEvent<*> | MouseEvent) => void) | null,
  userClick: ((SyntheticEvent<*> | MouseEvent) => void) | null,
|};

// A wrapper around a consumer
const useMentionsInteractionContext = (): Trackers => {
  const trackingStack = React.useContext(MentionsTrackingContext);
  const logInteraction = React.useContext(MentionsInteractionContext);
  const feedClick = React.useCallback(
    event => {
      const { sectionId, itemId } = aggregateTrackingStack(trackingStack);
      if (!sectionId) {
        Logging.error('No section id was provided. This is a no-op!');
        return;
      }
      if (!itemId) {
        Logging.error('No item id was provided. This is a no-op!');
      }
      if (logInteraction) {
        logInteraction((event: any), 'feedClick', { feedSectionId: sectionId, itemId });
      }
    },
    [logInteraction, trackingStack],
  );

  const showMore = React.useCallback(
    event => {
      const { impressionId } = aggregateTrackingStack(trackingStack);
      if (!impressionId) {
        Logging.error('No impression id was provided. This is a no-op!');
        return;
      }
      if (logInteraction) {
        logInteraction((event: any), 'showMore', { mixerImpressionId: impressionId });
      }
    },
    [logInteraction, trackingStack],
  );

  const userClick = React.useCallback(
    event => {
      const { sectionId, itemId, actor } = aggregateTrackingStack(trackingStack);
      if (!sectionId) {
        Logging.error('No section id was provided. This is a no-op!');
        return;
      }
      if (!itemId) {
        Logging.error('No item id was provided. This is a no-op!');
        return;
      }
      if (!actor) {
        Logging.error('No actor was provided. This is a no-op!');
        return;
      }
      if (logInteraction) {
        logInteraction((event: any), 'userClick', {
          feedSectionId: sectionId,
          itemId,
          userId: actor,
        });
      }
    },
    [logInteraction, trackingStack],
  );

  return React.useMemo(
    () =>
      logInteraction
        ? { feedClick, showMore, userClick }
        : { feedClick: null, showMore: null, userClick: null },
    [logInteraction, feedClick, showMore, userClick],
  );
};

export default useMentionsInteractionContext;
